/*
 *   File : onboarding-invest.js
 *   Author : https://evoqins.com
 *   Description : Onboarding investment page shown after PIN is set
 *   Integrations : NA
 *   Version : 1.0.0
*/

// *********** React Core ***************//
import { useEffect, useState } from "react";

// *********** Third Party ***************//
import { Modal } from "bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

// *********** Components ***************//
import { BannerButton } from "../../Component/Buttons";
import { FundCard, VideoCard } from "../../Component/Cards";
import { CustomAmountInput, CustomSlider, CustomTextInput } from "../../Component/Form-Elements";
import { Icon } from "../../Component/Icon";
import OnboardingVideoModal from "../../Component/Modal/onboardingVideoModal";
import FetchPortfolioHealthModal from "../../Component/Modal/fetch-portfolio-health";
import UploadCasQrCode from "../../Component/Modal/upload-cas-qr-code";
import { APIService } from "../../Service";
import { _formatToRupees } from "../../Helper";
import { _generateMFImportURL } from "../../Helper/api";

// *********** Assets ***************//
import PortfolioBannerImage from "../../Assets/Images/new-onboarding/pms-check-banner-img.png";
import HighReturnIcon from "../../Assets/Images/new-onboarding/collection/high-return.svg";
import SipWith100Icon from "../../Assets/Images/new-onboarding/collection/sip-with-100.svg";
import GoldSilverIcon from "../../Assets/Images/new-onboarding/collection/gold-silver.svg";
import EquityIcon from "../../Assets/Images/new-onboarding/collection/equity.svg";
import DebtIcon from "../../Assets/Images/new-onboarding/collection/debt.svg";
import MidCapIcon from "../../Assets/Images/new-onboarding/collection/mid-cap.svg";
import WealthHelpText from "../../Assets/Images/new-onboarding/wealth-help-text.svg";
import GoalRetirementCard from "../../Assets/Images/new-onboarding/goal-retirement-card.png";
import GoalEducationCard from "../../Assets/Images/new-onboarding/goal-education-card.png";

// Assumed annual return for "What happens if you wait?", same as the Nifty 50 long-term average used below
const EXPECTED_RETURN_RATE = 11;
// Height in px of the taller bar, the other bar is scaled against it
const BAR_MAX_HEIGHT = 160;
const BAR_MIN_HEIGHT = 8;
// Same idea for the "What happens if you wait?" bars
const WAIT_BAR_MAX_HEIGHT = 120;

// "See your money grow" calculator, return rate is shown in its footnote
const GROWTH_RETURN_RATE = 11;
const GROWTH_SIP_MIN = 500;
const GROWTH_SIP_MAX = 30000;
const GROWTH_SIP_STEP = 500;
const GROWTH_YEARS_MIN = 1;
const GROWTH_YEARS_MAX = 15;

// Active funds comparison, the index fund grows at GROWTH_RETURN_RATE (11%) and the active fund at its own rate.
// Both rates are what the fund returns to the investor, the expense ratios are only quoted in the note
const ACTIVE_FUND_RETURN_RATE = 8;
const INDEX_FUND_EXPENSE_RATIO = 0.75;
const ACTIVE_FUND_EXPENSE_RATIO = 2.25;
// The money lost to fees is also shown as this many SIPs of this amount
const LOSS_SIP_AMOUNT = 5000;
const ONE_LAKH = 100000;

const STAR_POSITIONS = [1, 2, 3, 4, 5];

// "What could this wealth help you achieve?" cards, id is the backend goal_id the goal page loads
const GOAL_CARDS = [
    { id: 2, path: "/goals/retirement", image: GoalRetirementCard, width: 332, height: 96, alt: "Plan your early retirement" },
    { id: 4, path: "/goals/kids-education", image: GoalEducationCard, width: 495, height: 144, alt: "Child’s Higher Education" },
];

// "filter" is the scheme/list request the Explore page sends (page_num is added there), "categories" are the
// category codes it selects under "Filter by category".
// Gold & Silver keeps sort 3, the default AUM sort returns "No data found" for funds without AUM
const COLLECTIONS = [
    { id: "high-return", name: "High Return", icon: HighReturnIcon, filter: { sort: 3 }, categories: [] },
    { id: "sip-with-100", name: "SIP With ₹100", icon: SipWith100Icon, filter: { sip_100: true }, categories: [] },
    { id: "gold-silver", name: "Gold & Silver", icon: GoldSilverIcon, filter: { fund_category: "GOLD/SILVER", sort: 3 }, categories: [] },
    { id: "equity", name: "Equity", icon: EquityIcon, filter: { fund_category: "EQUITY" }, categories: ["EQUITY"] },
    { id: "debt", name: "Debt", icon: DebtIcon, filter: { fund_category: "DEBT" }, categories: ["DEBT"] },
    { id: "mid-cap", name: "Mid Cap", icon: MidCapIcon, filter: { scheme_category: ["Mid Cap"] }, categories: ["Mid Cap"] },
];

// Future value of a monthly SIP, payments made at the start of each month
const _calculateSipValue = (monthly_amount, years, annual_rate = EXPECTED_RETURN_RATE) => {
    const monthly_rate = annual_rate / 12 / 100;
    const months = Math.max(years, 0) * 12;
    return monthly_amount * (((Math.pow(1 + monthly_rate, months) - 1) / monthly_rate) * (1 + monthly_rate));
};

// Bar height scaled against the tallest bar
const _getBarHeight = (value, peak_value) => {
    if (peak_value <= 0) {
        return BAR_MIN_HEIGHT;
    }
    return Math.max(BAR_MAX_HEIGHT * value / peak_value, BAR_MIN_HEIGHT);
};

const OnboardingInvest = () => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [currentAge, setCurrentAge] = useState("28");
    const [retirementAge, setRetirementAge] = useState("60");
    const [monthlySip, setMonthlySip] = useState(10000);
    const [delayYears, setDelayYears] = useState("3");
    const [growthSip, setGrowthSip] = useState(10000);
    const [growthYears, setGrowthYears] = useState(5);
    const [isActiveFundsCompared, setIsActiveFundsCompared] = useState(false);
    const [isMarqueePaused, setIsMarqueePaused] = useState(false);
    const [isImportLoading, setIsImportLoading] = useState(false);
    const [showUploadMFQRModal, setShowUploadMFQRModal] = useState(false);
    const [homeData, setHomeData] = useState({});
    const [currentVideo, setCurrentVideo] = useState(null);

    useEffect(() => {
        _getHomeDetails();
    }, []);

    // MF Central redirects back here with the import status, the QR code is uploaded next
    useEffect(() => {
        const status = searchParams.get("status");
        const reqId = searchParams.get("reqId");

        if (status && reqId) {
            setShowUploadMFQRModal(true);
        }
    }, [searchParams]);

    // Open the QR code upload modal once it is rendered
    useEffect(() => {
        if (showUploadMFQRModal === true) {
            new Modal(document.getElementById("upload-mf-qr-code-modal")).show();
        }
    }, [showUploadMFQRModal]);

    // Open the video modal once a video is picked
    useEffect(() => {
        if (currentVideo !== null) {
            new Modal(document.querySelector("#onboarding-video")).show();
        }
    }, [currentVideo]);

    // API - Get home data, funds in focus, testimonials and expert videos
    const _getHomeDetails = () => {
        let is_post = false;
        let url = "home/get";

        APIService(is_post, url, null, null).then((response) => {
            if (response.status_code === 200) {
                setHomeData(response.data);
            } else {
                setHomeData({});
                toast.dismiss();
                toast.error(response.message);
            }
        });
    };

    const fund_list = homeData.fund_list || [];
    const testimonials = homeData.testimonials || [];
    const videos = homeData.videos || [];

    const investing_years = Number(retirementAge) - Number(currentAge);
    const start_today_value = _calculateSipValue(monthlySip, investing_years);
    const delayed_value = _calculateSipValue(monthlySip, investing_years - Number(delayYears));
    const delay_cost = start_today_value - delayed_value;
    const delayed_bar_height = start_today_value > 0 ? Math.max(WAIT_BAR_MAX_HEIGHT * delayed_value / start_today_value, BAR_MIN_HEIGHT) : BAR_MIN_HEIGHT;

    const growth_invested_amount = Number(growthSip) * Number(growthYears) * 12;
    const growth_current_value = _calculateSipValue(Number(growthSip), Number(growthYears), GROWTH_RETURN_RATE);
    const growth_gain = growth_current_value - growth_invested_amount;

    // The green bar is the index fund, so the loss is the gap between it and the active fund
    const active_fund_value = _calculateSipValue(Number(growthSip), Number(growthYears), ACTIVE_FUND_RETURN_RATE);
    const fee_loss = growth_current_value - active_fund_value;
    const fee_loss_percentage = growth_current_value > 0 ? Math.round(fee_loss * 100 / growth_current_value) : 0;
    const fee_loss_sips = Math.round(fee_loss / LOSS_SIP_AMOUNT);

    const growth_peak_label_class = isActiveFundsCompared ? "growth-bar-label-start" : "growth-bar-label-end";
    const growth_bars_gap_class = isActiveFundsCompared ? "gap-3" : "gap-30px";
    const growth_invested_bar_height = _getBarHeight(growth_invested_amount, growth_current_value);
    const active_fund_bar_height = _getBarHeight(active_fund_value, growth_current_value);

    const _handleSeeAllFunds = () => {
        navigate("/explore");
    };

    const _handleFundClick = (fund) => {
        navigate("/explore/fund-details", {
            state: {
                fund_id: fund.mf_id
            }
        });
    };

    // The goal pages read the goal id from the route state
    const _handleGoalClick = (goal) => {
        navigate(goal.path, {
            state: {
                goal_id: goal.id
            }
        });
    };

    const _handleAnalyzePortfolio = () => {
        Modal.getOrCreateInstance(document.querySelector("#fetch-portfolio-health-modal")).show();
    };

    const _handleSuccessExternalImport = () => {
        navigate("/portfolio-check");
    };

    // Opens MF Central in a new window and the QR code upload modal here, for the code downloaded from MF Central
    const _handleContinueFetchPortfolio = () => {
        setIsImportLoading(true);

        _generateMFImportURL().then((response) => {
            if (response?.status_code === 200) {
                const mf_central_window = window.open(response?.data?.redirectUrl, "_blank");

                if (mf_central_window === null) {
                    toast.dismiss();
                    toast.error("Please allow pop-ups to continue");
                } else {
                    // Swap to the QR code upload modal once this one has closed
                    const fetch_modal = document.querySelector("#fetch-portfolio-health-modal");
                    fetch_modal.addEventListener("hidden.bs.modal", () => setShowUploadMFQRModal(true), { once: true });
                    Modal.getOrCreateInstance(fetch_modal).hide();
                }
            } else {
                toast.dismiss();
                toast.error(response?.message);
            }
            setIsImportLoading(false);
        });
    };

    // PIN step already moved the tokens, the user is logged in so the dashboard is open to them
    const _handleSkipToDashboard = () => {
        navigate("/");
    };

    const _handleCollectionClick = (collection) => {
        navigate("/explore", {
            state: {
                onboarding: true,
                collection_filter: collection.filter,
                collection_categories: collection.categories
            }
        });
    };

    const _handleCurrentAgeChange = (value) => {
        setCurrentAge(value);
    };

    const _handleRetirementAgeChange = (value) => {
        setRetirementAge(value);
    };

    const _handleMonthlySipChange = (value) => {
        setMonthlySip(value);
    };

    const _handleDelayYearsChange = (value) => {
        setDelayYears(value);
    };

    const _handleGrowthSipChange = (value) => {
        setGrowthSip(value);
    };

    const _handleGrowthYearsChange = (value) => {
        setGrowthYears(value);
    };

    const _handleToggleActiveFunds = () => {
        setIsActiveFundsCompared((isCompared) => !isCompared);
    };

    const _handleCloseVideo = () => {
        setCurrentVideo(null);
    };

    const _handlePauseMarquee = () => {
        setIsMarqueePaused(true);
    };

    const _handleResumeMarquee = () => {
        setIsMarqueePaused(false);
    };

    const testimonial_cards = testimonials.map((item) => (
        <article className="w-270 flex-shrink-0 e-bg-white e-border-1px-white-marble e-border-radius-12 padding-16px-all" key={item.id}>
            <p className="e-text-neutral-navy e-primary-font-inter e-font-weight-400 e-font-14 line-height-20px margin-10px-bottom">
                {item.testimonial}
            </p>
            <p className="d-flex gap-1 e-font-12 line-height-16px mb-3" role="img" aria-label={`${item.rating} out of 5 stars`}>
                {
                    STAR_POSITIONS.map((position) => (
                        <span className={position <= item.rating ? "e-text-hunyadi-yellow" : "e-text-sonic-silver"} aria-hidden="true" key={position}>
                            ★
                        </span>
                    ))
                }
            </p>
            <div className="d-flex align-items-center gap-10px">
                <img src={item.image}
                    width={28}
                    height={28}
                    className="flex-shrink-0 w-28px h-28 object-fit-cover e-border-radius-50per"
                    draggable="false"
                    alt="" />
                <div>
                    <p className="e-text-neutral-navy e-primary-font-inter e-font-weight-400 e-font-12 line-height-18px mb-0">
                        {item.name}
                    </p>
                    <p className="e-text-html-css-gray e-primary-font-inter e-font-weight-400 e-font-12 line-height-18px mb-0">
                        {item.investing_since}
                    </p>
                </div>
            </div>
        </article>
    ));

    return (
        <section className="row px-4 px-sm-5 ps-lg-4 pe-lg-0">
            <div className="col-lg-11 e-bg-white e-border-radius-24 p-sm-4 p-3">

                {/* Portfolio banner */}
                <article className="d-flex gap-3 e-bg-night e-border-radius-16 overflow-hidden mb-4">
                    <div className="col d-flex flex-column align-items-start gap-3 padding-32px-all padding-16px-all-res">
                        <h2 className="e-text-hunyadi-yellow e-primary-font-inter e-font-weight-600 e-font-16-res e-font-20 e-line-height-100 mb-0">
                            Analyze your portfolio
                        </h2>
                        <p className="e-text-off-white e-primary-font-inter e-font-weight-300 e-font-18-res e-font-24 e-line-height-100 mb-0">
                            Fetch your portfolio. Check its health in under a minute.
                        </p>
                        <BannerButton name="Analyze portfolio"
                            onPress={_handleAnalyzePortfolio} />
                    </div>
                    <img src={PortfolioBannerImage}
                        width={642}
                        height={437}
                        className="col-4 h-auto d-none d-sm-block"
                        draggable="false"
                        alt="Vault with a gold rupee coin" />
                </article>

                {/* Index in focus */}
                {
                    fund_list.length > 0 &&
                    <section className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16-res e-font-20 e-line-height-100 mb-0">
                                Index in focus
                            </h2>
                            <button type="button"
                                className="e-bg-transparent no-border no-outline p-0 e-text-egyptian-blue e-primary-font-inter e-font-weight-600 e-font-14 e-line-height-100 cursor-pointer text-decoration-hover-underline"
                                onClick={_handleSeeAllFunds}>
                                See all funds
                            </button>
                        </div>
                        <div className="row row-gap-3">
                            {
                                fund_list.map((item) => (
                                    <div className="col-12 col-sm-6 col-xl-4" key={item.mf_id}>
                                        <FundCard type={2}
                                            data={item}
                                            className="card-hover"
                                            handleInvest={_handleFundClick} />
                                    </div>
                                ))
                            }
                        </div>
                    </section>
                }

                {/* Collections */}
                <section className="row row-gap-4 mb-4">
                    <div className="col-12 col-lg-7">
                        <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16-res e-font-20 e-line-height-100 mb-3">
                            Collections
                        </h2>
                        <ul className="row row-cols-2 row-cols-sm-3 g-3 list-unstyled mb-0">
                            {
                                COLLECTIONS.map((item) => (
                                    <li className="col" key={item.id}>
                                        <button type="button"
                                            className="w-100 d-flex flex-column align-items-center gap-3 p-0 e-bg-transparent no-border no-outline cursor-pointer"
                                            onClick={() => _handleCollectionClick(item)}>
                                            <span className="d-flex align-items-center justify-content-center w-100 h-152px h-104px-res-576px p-2 e-bg-seasalt e-border-radius-32 e-border-radius-16-res">
                                                <img src={item.icon}
                                                    className="img-fluid"
                                                    draggable="false"
                                                    alt="" />
                                            </span>
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-14-res e-font-16 line-height-18px-res line-height-20px text-center">
                                                {item.name}
                                            </span>
                                        </button>
                                    </li>
                                ))
                            }
                        </ul>

                        {/* See your money grow */}
                        <section className="e-bg-alice-azure e-border-1px-baby-blue e-border-radius-16 margin-32px-top p-sm-4 p-3">
                            <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16-res e-font-18 e-line-height-100 mb-3">
                                See your money grow
                            </h2>
                            <div className="growth-layout">
                                <article className="growth-layout-inputs e-bg-azure-tint e-border-1px-baby-blue e-border-radius-16 py-3 padding-12px-lr">
                                    <label className="d-flex flex-wrap flex-xl-nowrap justify-content-between align-items-center gap-1 row-gap-2">
                                        <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-14 line-height-20px">
                                            Monthly SIP
                                        </span>
                                        <CustomAmountInput variant={3}
                                            className="w-98px flex-shrink-0"
                                            inputClassName="e-bg-white"
                                            value={growthSip}
                                            maxValue={10000000}
                                            borderClassName="e-border-1px-gainsboro-gray"
                                            errorMsg=""
                                            handleChange={_handleGrowthSipChange} />
                                    </label>
                                    <div className="h-4 margin-12px-top mb-4">
                                        <CustomSlider type={2}
                                            min={GROWTH_SIP_MIN}
                                            max={GROWTH_SIP_MAX}
                                            step={GROWTH_SIP_STEP}
                                            defaultValue={Number(growthSip)}
                                            handleChange={_handleGrowthSipChange} />
                                    </div>

                                    <label className="d-flex flex-wrap flex-xl-nowrap justify-content-between align-items-center gap-1 row-gap-2">
                                        <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-14 line-height-20px">
                                            Investment Time
                                        </span>
                                        <CustomTextInput variant={3}
                                            type="number"
                                            className="text-center"
                                            wrapperClassName="w-98px flex-shrink-0 e-bg-white padding-12px-lr"
                                            inputWrapperBorderStyle="e-border-radius-12"
                                            value={growthYears}
                                            maxLength={2}
                                            hasBottomMargin={false}
                                            borderClassName="e-border-1px-gainsboro-gray"
                                            errorMsg=""
                                            successMsg=""
                                            handleChange={_handleGrowthYearsChange} />
                                    </label>
                                    <div className="h-4 margin-12px-top mb-4">
                                        <CustomSlider type={2}
                                            min={GROWTH_YEARS_MIN}
                                            max={GROWTH_YEARS_MAX}
                                            step={1}
                                            defaultValue={Number(growthYears)}
                                            handleChange={_handleGrowthYearsChange} />
                                    </div>

                                    <p className="e-text-azure-link e-alt-font-poppins e-font-weight-400 e-font-12 mb-0">
                                        Assumes {GROWTH_RETURN_RATE}% p.a., aligned with Nifty 50’s long-term average. Actual returns may vary.
                                    </p>
                                </article>

                                {/* Hidden cost of active funds */}
                                {
                                    isActiveFundsCompared &&
                                    <article className="growth-layout-cost e-bg-linen-peach e-border-1px-flavescent e-box-shadow-soft-card e-border-radius-16 padding-16px-all mt-3 mb-1">
                                        <h3 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-12 e-line-height-100 margin-20px-bottom">
                                            The hidden cost of active funds
                                        </h3>
                                        <ul className="d-flex flex-wrap gap-3 list-unstyled mb-4">
                                            <li className="col e-bg-white e-box-shadow-soft-card e-border-radius-8 text-center py-2 px-0">
                                                <p className="e-text-smoky-black e-primary-font-inter e-font-weight-400 e-font-12 e-line-height-100 mb-1">
                                                    Index fund
                                                </p>
                                                <p className="e-text-mint-green e-primary-font-inter e-font-weight-700 e-font-14 e-line-height-100 mb-0">
                                                    ₹{_formatToRupees(growth_current_value)}
                                                </p>
                                            </li>
                                            <li className="col e-bg-white e-box-shadow-soft-card e-border-radius-8 text-center py-2 px-0">
                                                <p className="e-text-smoky-black e-primary-font-inter e-font-weight-400 e-font-12 e-line-height-100 mb-1">
                                                    Active fund
                                                </p>
                                                <p className="e-text-imperial-red e-primary-font-inter e-font-weight-700 e-font-14 e-line-height-100 mb-0">
                                                    ₹{_formatToRupees(active_fund_value)}
                                                </p>
                                            </li>
                                        </ul>
                                        <p className="e-text-imperial-red e-primary-font-inter e-font-weight-700 e-font-18 line-height-24px text-center mb-1">
                                            ₹{_formatToRupees(fee_loss)}
                                        </p>
                                        <p className="e-text-smoky-black-60 e-primary-font-inter e-font-weight-300 e-font-12 e-line-height-100 text-center margin-12px-bottom">
                                            That’s {fee_loss_percentage}% less of your final corpus.
                                        </p>
                                        <p className="e-bg-bisque-peach e-border-radius-8 padding-8px-all e-text-black e-primary-font-inter e-font-weight-400 e-font-10 line-height-14px mb-0">
                                            ₹{(fee_loss / ONE_LAKH).toFixed(2)}L less wealth = ~{fee_loss_sips} × ₹{_formatToRupees(LOSS_SIP_AMOUNT)} SIPs, driven by ~{INDEX_FUND_EXPENSE_RATIO}% vs ~{ACTIVE_FUND_EXPENSE_RATIO}% annual fund expenses.
                                        </p>
                                    </article>
                                }

                                {/* Growth chart */}
                                <div className="growth-layout-chart d-flex flex-column">
                                    <p className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-14 line-height-22px mb-4">
                                        A <span className="e-font-weight-700">₹{_formatToRupees(growthSip)}</span> SIP for {growthYears || 0} years investment boosts your potential by <span className="e-font-weight-700">₹{_formatToRupees(growth_gain)}</span>
                                    </p>
                                    <div className="growth-chart d-flex flex-column flex-grow-1">
                                        <ul className="d-flex gap-3 list-unstyled mb-5">
                                            <li className="d-flex align-items-center gap-2 e-text-smoky-black e-primary-font-inter e-font-weight-400 e-font-10 line-height-14px">
                                                <span className="w-8px h-8px flex-shrink-0 e-border-radius-50per e-bg-peach-cream" />
                                                Invested amount
                                            </li>
                                            <li className="d-flex align-items-center gap-2 e-text-smoky-black e-primary-font-inter e-font-weight-400 e-font-10 line-height-14px">
                                                <span className="w-8px h-8px flex-shrink-0 e-border-radius-50per e-bg-emerald" />
                                                Current value
                                            </li>
                                            {
                                                isActiveFundsCompared &&
                                                <li className="d-flex align-items-center gap-2 e-text-smoky-black e-primary-font-inter e-font-weight-400 e-font-10 line-height-14px">
                                                    <span className="w-8px h-8px flex-shrink-0 e-border-radius-50per e-bg-imperial-red" />
                                                    Active fund
                                                </li>
                                            }
                                        </ul>
                                        <ul className={`d-flex justify-content-center align-items-end ${growth_bars_gap_class} list-unstyled px-5 mb-0`}>
                                            <li className="position-relative">
                                                <div className="w-40px e-border-radius-8 e-bg-invested-bar-gradient"
                                                    style={{ height: growth_invested_bar_height }} />
                                                <span className="growth-bar-label-start position-absolute top-0 mt-2 text-nowrap e-bg-mint-cream e-border-radius-22 padding-6px-all e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-13-37 e-line-height-100">
                                                    ₹{_formatToRupees(growth_invested_amount)}
                                                </span>
                                            </li>
                                            <li className="position-relative">
                                                <div className="w-40px e-border-radius-8 e-bg-current-bar-gradient"
                                                    style={{ height: BAR_MAX_HEIGHT }} />
                                                <span className={`${growth_peak_label_class} position-absolute top-0 mt-2 text-nowrap e-bg-mint-cream e-border-radius-22 padding-6px-all e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-13-37 e-line-height-100`}>
                                                    ₹{_formatToRupees(growth_current_value)}
                                                </span>
                                            </li>
                                            {
                                                isActiveFundsCompared &&
                                                <li className="position-relative">
                                                    <div className="w-40px e-border-radius-8 e-bg-active-bar-gradient"
                                                        style={{ height: active_fund_bar_height }} />
                                                    <span className="growth-bar-label-end position-absolute top-0 mt-2 text-nowrap e-bg-mint-cream e-border-radius-22 padding-6px-all e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-13-37 e-line-height-100">
                                                        ₹{_formatToRupees(active_fund_value)}
                                                    </span>
                                                </li>
                                            }
                                        </ul>
                                        {
                                            isActiveFundsCompared &&
                                            <div className="text-center mt-4">
                                                <p className="e-text-imperial-red e-primary-font-inter e-font-weight-700 e-font-14 line-height-18px mb-1">
                                                    ₹{_formatToRupees(fee_loss)}
                                                </p>
                                                <p className="e-text-smoky-black-60 e-primary-font-inter e-font-weight-300 e-font-12 line-height-16px mb-0">
                                                    Lost to higher fees over {growthYears || 0} years
                                                </p>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>

                            {/* Active funds comparison */}
                            <div className="d-flex justify-content-between align-items-center gap-3 e-bg-white e-border-radius-16 e-box-shadow-soft-card px-3 py-2 margin-12px-top">
                                <label htmlFor="compare-active-funds" className="cursor-pointer">
                                    <span className="d-block e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-12 line-height-16px">
                                        Want to see how active funds compare?
                                    </span>
                                    <span className="d-block e-text-smoky-black-60 e-primary-font-inter e-font-weight-300 e-font-12 line-height-14px">
                                        See the potential difference due to higher fees
                                    </span>
                                </label>
                                <div className="form-check form-switch mb-0 ps-0 d-flex align-items-center">
                                    <input id="compare-active-funds"
                                        className="cursor-pointer form-check-input toggle-40x20 flex-shrink-0 m-0 shadow-none"
                                        type="checkbox"
                                        role="switch"
                                        checked={isActiveFundsCompared}
                                        onChange={_handleToggleActiveFunds} />
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Wealth help */}
                    <div className="col-lg-5">
                        <article className="e-bg-white e-border-1px-gainsboro-50 e-border-radius-16 p-3">
                            <h2 className="text-center margin-12px-bottom">
                                <img src={WealthHelpText}
                                    width={310}
                                    height={50}
                                    className="img-fluid"
                                    draggable="false"
                                    alt="What could this wealth help you achieve?" />
                            </h2>
                            <div className="e-bg-asset-mix-card e-border-radius-16 padding-12px-all">
                                <p className="e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px margin-12px-bottom">
                                    By investing <span className="e-text-azure-blue e-font-weight-800">₹10,000</span> every month for <span className="e-font-weight-600">5 years</span>, you could build enough wealth to fund your apartment down payment or make real progress towards child’s higher education.
                                </p>
                                <ul className="goal-card-list d-flex gap-4 list-unstyled mb-0">
                                    {
                                        GOAL_CARDS.map((item) => (
                                            <li className="col" key={item.id}>
                                                <button type="button"
                                                    className="d-block w-100 p-0 e-bg-transparent no-border no-outline cursor-pointer card-hover"
                                                    onClick={() => _handleGoalClick(item)}>
                                                    <img src={item.image}
                                                        width={item.width}
                                                        height={item.height}
                                                        className="w-100 h-auto"
                                                        draggable="false"
                                                        alt={item.alt} />
                                                </button>
                                            </li>
                                        ))
                                    }
                                </ul>
                            </div>
                        </article>

                        {/* Cost of waiting */}
                        <article className="e-bg-emerald-card-gradient e-border-1px-anti-flash-gray e-border-radius-20 mt-4">
                            <div className="px-sm-4 px-3 pt-4">
                                <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-18 e-line-height-100 margin-32px-bottom">
                                    What happens if you wait?
                                </h2>
                                <div className="d-flex flex-column gap-2">
                                    <div className="row  flex-column flex-sm-row gx-2">
                                        <div className="col-sm-6">
                                            <CustomTextInput label="Current age"
                                                variant={2}
                                                type="number"
                                                value={currentAge}
                                                maxLength={2}
                                                hasBottomMargin={false}
                                                errorMsg=""
                                                successMsg=""
                                                handleChange={_handleCurrentAgeChange} />
                                        </div>
                                        <div className="col-sm-6">
                                            <CustomTextInput label="Retirement age"
                                                variant={2}
                                                type="number"
                                                value={retirementAge}
                                                maxLength={2}
                                                hasBottomMargin={false}
                                                errorMsg=""
                                                successMsg=""
                                                handleChange={_handleRetirementAgeChange} />
                                        </div>
                                    </div>
                                    <CustomAmountInput label="Monthly SIP"
                                        variant={2}
                                        value={monthlySip}
                                        maxValue={10000000}
                                        errorMsg=""
                                        handleChange={_handleMonthlySipChange} />
                                    <div>
                                        <CustomTextInput label="Delay in starting (in years )"
                                            variant={2}
                                            type="number"
                                            value={delayYears}
                                            maxLength={2}
                                            hasBottomMargin={false}
                                            errorMsg=""
                                            successMsg=""
                                            handleChange={_handleDelayYearsChange} />
                                    </div>
                                </div>
                            </div>

                            {/* Returns comparison */}
                            <div className="px-3 pb-3 pt-4">
                                <div className="d-flex justify-content-center align-items-end gap-3 mb-4">
                                    <div className="text-center">
                                        <p className="e-text-dark-aubergine e-primary-font-inter e-font-weight-700 e-font-12 line-height-16px mb-0">
                                            If delayed
                                        </p>
                                        <p className="e-text-light-red e-primary-font-inter e-font-weight-700 e-font-14 line-height-20px margin-12px-bottom">
                                            ₹{_formatToRupees(delayed_value)}
                                        </p>
                                        <div className="progress progress-vertical w-64px e-border-radius-8" style={{ height: delayed_bar_height }}>
                                            <div className="progress-bar h-100 e-border-radius-8 e-bg-red-fade-gradient" />
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <p className="e-text-dark-aubergine e-primary-font-inter e-font-weight-700 e-font-12 line-height-16px mb-0">
                                            Start today
                                        </p>
                                        <p className="e-text-bottle-green e-primary-font-inter e-font-weight-700 e-font-14 line-height-20px margin-12px-bottom">
                                            ₹{_formatToRupees(start_today_value)}
                                        </p>
                                        <div className="progress progress-vertical w-64px e-border-radius-8" style={{ height: WAIT_BAR_MAX_HEIGHT }}>
                                            <div className="progress-bar h-100 e-border-radius-8 e-bg-green-fade-gradient" />
                                        </div>
                                    </div>
                                </div>

                                <p className="d-flex align-items-start gap-2 e-bg-ghost-gray e-border-1px-linen-gray e-border-radius-16 padding-12px-all e-text-black e-primary-font-inter e-font-weight-400 e-font-14 line-height-20px mb-0">
                                    <Icon icon="info-circle-blue"
                                        size={16} />
                                    <span>
                                        Waiting just {delayYears || 0} years could cost you <span className="e-text-light-red e-font-weight-600">₹{_formatToRupees(delay_cost)}</span> in future wealth.
                                    </span>
                                </p>
                            </div>
                        </article>
                    </div>
                </section>

                {/* Testimonials */}
                {
                    testimonials.length > 0 &&
                    <section className="mt-4">
                        <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16-res e-font-20 line-height-24px mb-3">
                            <span className="e-text-royal-blue">Trusted by</span> <span className="e-font-weight-600">2M</span> users
                        </h2>
                        <div className={isMarqueePaused ? "e-marquee e-marquee-paused" : "e-marquee"}
                            onMouseEnter={_handlePauseMarquee}
                            onMouseLeave={_handleResumeMarquee}>
                            <div className="e-marquee-track">{testimonial_cards}</div>
                            <div className="e-marquee-track" aria-hidden="true">{testimonial_cards}</div>
                        </div>
                    </section>
                }

                {/* Expert videos */}
                {
                    videos.length > 0 &&
                    <section className="mt-4">
                        <h2 className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16-res e-font-20 line-height-24px mb-3">
                            Hear from our experts
                        </h2>
                        <div className="row g-3">
                            {
                                videos.map((item) => (
                                    <div className="col-12 col-sm-6 col-xl-4" key={item.video_id}>
                                        <VideoCard type={2}
                                            data={item}
                                            handleClickVideo={() => setCurrentVideo(item.video_id)} />
                                    </div>
                                ))
                            }
                        </div>
                    </section>
                }

                {/* Trust note */}
                <section className="text-center padding-56px-top">
                    <div className="row justify-content-center">
                        <div className="col-10 col-lg-7">
                            <p className="e-text-eerie-black-16per e-primary-font-inter e-font-weight-700 e-font-18-res e-font-32 line-height-24px-res line-height-42px margin-12px-bottom">
                                Trusted by over a million investors across India
                            </p>
                        </div>
                    </div>
                    <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 margin-12px-bottom">
                        <span className="text-nowrap e-text-dark-jet e-alt-font-poppins e-font-weight-400 e-font-14 line-height-16px">
                            AMFI & SEBI registered firm
                        </span>
                        <span className="text-nowrap d-flex gap-1 e-text-dark-jet e-alt-font-poppins e-font-weight-400 e-font-14 line-height-16px">
                            <Icon icon="arn-shild" size={"1rem"} />
                            ARN-272159
                        </span>
                    </div>
                    <button type="button"
                        className="e-bg-transparent e-border-1px-water-blue e-border-radius-20 padding-12px-lr padding-6px-tb e-text-egyptian-blue e-primary-font-inter e-font-weight-400 e-font-12 line-height-12px letter-spacing-neg-0-23px cursor-pointer no-outline"
                        onClick={_handleSkipToDashboard}>
                        Skip to dashboard
                    </button>
                </section>
            </div>

            {
                currentVideo !== null &&
                <OnboardingVideoModal data={currentVideo}
                    onClose={_handleCloseVideo} />
            }

            <FetchPortfolioHealthModal loading={isImportLoading}
                onContinue={_handleContinueFetchPortfolio} />

            {
                showUploadMFQRModal &&
                <UploadCasQrCode type={2}
                    status={searchParams.get("status") || "success"}
                    handleSuccessImport={_handleSuccessExternalImport}
                    handleModalClose={() => setShowUploadMFQRModal(false)} />
            }
        </section >
    );
};

export default OnboardingInvest;
